// torchlite/src/lib/torchSvg.ts
// v11 — Embers animate upward and outward from the flame tip.
// 1 holder = bare flame. Long chain = storm of flying embers.

export type TorchState = 'lit' | 'extinguished';

export interface AcceptLink {
  holderPubkey: string;
  zapAmountSats: number;
  mark?: string;
  positionIndex: number;
}

export interface TorchInputs {
  genesisId: string;
  chain: AcceptLink[];
  state: TorchState;
}

const byteAt = (hex: string, i: number) =>
  parseInt(hex.slice(i * 2, i * 2 + 2) || '00', 16);

function syncHash(msg: string): Uint8Array {
  const data = new TextEncoder().encode(msg);
  const h = new Uint8Array(32);
  let s = data.length;
  for (let i = 0; i < data.length; i++) {
    s = ((s << 5) - s + data[i]) | 0;
    h[i % 32] = (h[i % 32] + s + i) & 0xff;
  }
  for (let r = 0; r < 4; r++)
    for (let i = 0; i < 32; i++)
      h[i] = ((h[i] * 31 + h[(i + 7) % 32] * 17 + h[(i + 13) % 32] * 7 + r) & 0xff);
  return h;
}

const derive = (a: string, b: string) => syncHash(a + ':' + b);
const f = (n: number) => n.toFixed(2);

// Exact Lucide Flame path (24×24, closed)
const FLAME = `M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4z`;

export function renderTorch({ genesisId, chain, state }: TorchInputs): string {
  const uid = genesisId.slice(0, 8);
  const n   = chain.length;

  const hue = 14 + (byteAt(genesisId, 0) % 22);
  const hz  = 1.1 + (byteAt(genesisId, 2) % 10) / 7;

  const sat  = state === 'lit' ? 1    : 0;
  const op   = state === 'lit' ? 1    : 0.25;
  const gop  = state === 'lit' ? 0.72 : 0;
  const live = state === 'lit';
  const satF = sat < 1 ? `filter="url(#sat_${uid})"` : '';

  // ── Flame geometry ────────────────────────────────────────────
  const S   = 8.2;
  const TX  = 100 - 12 * S;
  const TY  = 60;   // flame sits lower — embers have room to fly above

  // Key flame coords in SVG space
  const flameTipX  = 100 + (byteAt(genesisId, 6) % 8 - 4); // slight lean
  const flameTipY  = TY + 3 * S;    // tip ≈ y=3 on 24-grid
  const flameBaseY = TY + 22 * S;   // base ≈ y=22 on 24-grid
  const flameCX    = 100;
  const flameCY    = TY + 14 * S;   // visual centre

  const glowR = 30 + Math.min(n * 1.4, 100);

  const c0 = `hsl(${hue},      96%, 46%)`;
  const c1 = `hsl(${hue + 15}, 98%, 60%)`;
  const c2 = `hsl(${hue + 26},100%, 75%)`;
  const c3 = `hsl(${hue + 34},100%, 92%)`;
  const cg = `hsl(${hue + 8},  90%, 54%)`;

  // ── Embers ────────────────────────────────────────────────────
  // Each ember:
  //   - starts near the flame tip
  //   - animates upward + sideways (cx and cy both animate)
  //   - fades from bright → transparent as it rises
  //   - loops with a random duration and delay so they stagger
  //
  // At chain=1: 1 ember. At chain=50: 50 embers. Cap at 60.
  const MAX_EMBERS = 60;
  const emberLinks = chain.slice(0, MAX_EMBERS);

  const emberEls = emberLinks.map((link, i) => {
    const seed = derive(link.holderPubkey, genesisId);

    // Color: strictly warm, orange→yellow→white
    const eHue = hue + (byteAt(seed, 3) % 36) - 6;
    const eSat = 85 + byteAt(seed, 4) % 12;
    const eLit = 60 + byteAt(seed, 5) % 30;
    const ec   = `hsl(${f(Math.max(8, eHue))},${eSat}%,${eLit}%)`;

    // Travel path: ember starts near tip, drifts sideways, rises upward
    // startX: near the flame tip with a spread that grows with chain length
    const spread = 8 + Math.min(n * 0.5, 40);
    const xOff   = (byteAt(seed, 0) / 255) * 2 - 1;  // -1 to +1
    const startX = flameTipX + xOff * spread * 0.4;
    const startY = flameTipY + (byteAt(seed, 1) / 255) * S * 4; // start in upper flame

    // End position: rises 40–120px above start, drifts sideways
    const riseAmt = 40 + (byteAt(seed, 7) / 255) * 80;
    const driftX  = xOff * (15 + (byteAt(seed, 8) / 255) * spread);
    const endX    = startX + driftX;
    const endY    = startY - riseAmt;

    // Midpoint for a slight arc
    const midX = startX + driftX * 0.4 + (byteAt(seed, 9) % 10 - 5);
    const midY = startY - riseAmt * 0.5;

    // Duration: 1.0 – 2.8s per cycle
    const dur = f(1.0 + (byteAt(seed, 10) / 255) * 1.8);

    // Delay offset (begin time) so embers don't all start together
    const delay = f((byteAt(seed, 11) / 255) * 3.0);

    // Negative begin = start mid-flight immediately, no static pause before first cycle
    const begin = `-${delay}s`;

    // Only animate when lit — extinguished embers shown grey and still
    const motion = live ? `
      <animate attributeName="cx"
        values="${f(startX)};${f(midX)};${f(endX)}"
        dur="${dur}s" begin="${begin}" repeatCount="indefinite"
        keyTimes="0;0.45;1" calcMode="spline"
        keySplines="0.4 0 0.6 1;0.4 0 0.6 1"/>
      <animate attributeName="cy"
        values="${f(startY)};${f(midY)};${f(endY)}"
        dur="${dur}s" begin="${begin}" repeatCount="indefinite"
        keyTimes="0;0.45;1" calcMode="spline"
        keySplines="0.4 0 0.6 1;0.2 0 0.4 1"/>
      <animate attributeName="opacity"
        values="${f(op)};${f(op * 0.85)};0"
        dur="${dur}s" begin="${begin}" repeatCount="indefinite"
        keyTimes="0;0.5;1" calcMode="spline"
        keySplines="0.4 0 0.6 1;0.4 0 0.2 1"/>` : '';

    const staticOp = live ? f(op) : f(op * 0.4);

    // Tiny fixed dot — no glow halo
    return `
  <circle cx="${f(startX)}" cy="${f(startY)}" r="1.5"
          fill="${ec}" opacity="${staticOp}" ${satF}>
    ${motion}
  </circle>`;
  }).join('\n');

  // ── Main flame animations ─────────────────────────────────────
  const fd = f(1.9 / hz);
  const mainFlicker = live ? `
    <animateTransform attributeName="transform" type="scale"
      values="1 1;1.013 0.994;0.993 1.010;1.007 0.997;1 1"
      dur="${fd}s" repeatCount="indefinite" additive="sum"/>` : '';

  const glowAnim = live ? `
    <animate attributeName="r"
      values="${f(glowR)};${f(glowR * 1.18)};${f(glowR * 0.88)};${f(glowR * 1.08)};${f(glowR)}"
      dur="${f(2.2 / hz)}s" repeatCount="indefinite"/>
    <animate attributeName="opacity"
      values="${f(gop)};${f(gop * 1.22)};${f(gop * 0.80)};${f(gop * 1.1)};${f(gop)}"
      dur="${f(2.2 / hz)}s" repeatCount="indefinite"/>` : '';

  // ViewBox — fixed height, embers fly within a generous space above the flame
  const vTop = TY - 120;  // room for embers to fly up
  const vBot = flameBaseY + 20;
  const vH   = vBot - vTop;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-20 ${f(vTop)} 240 ${f(vH)}"
  role="img" aria-label="Torch — ${n} holder${n !== 1 ? 's' : ''}, ${state}">
  <defs>
    <filter id="sat_${uid}"><feColorMatrix type="saturate" values="${sat}"/></filter>
    <radialGradient id="glow_${uid}" cx="50%" cy="50%" r="50%">
      <stop offset="0%"   stop-color="${cg}"  stop-opacity="1"/>
      <stop offset="42%"  stop-color="${c0}"  stop-opacity="0.55"/>
      <stop offset="100%" stop-color="${c0}"  stop-opacity="0"/>
    </radialGradient>
    <linearGradient id="fg_${uid}" x1="0%" y1="100%" x2="0%" y2="0%">
      <stop offset="0%"   stop-color="${c0}"/>
      <stop offset="38%"  stop-color="${c1}"/>
      <stop offset="72%"  stop-color="${c2}"/>
      <stop offset="100%" stop-color="${c3}"/>
    </linearGradient>
    <linearGradient id="ig_${uid}" x1="0%" y1="100%" x2="0%" y2="0%">
      <stop offset="0%"   stop-color="${c1}"/>
      <stop offset="58%"  stop-color="${c2}"/>
      <stop offset="100%" stop-color="${c3}"/>
    </linearGradient>
  </defs>

  <!-- Glow -->
  <circle cx="${f(flameCX)}" cy="${f(flameCY)}" r="${f(glowR)}"
          fill="url(#glow_${uid})" opacity="${f(gop)}" ${satF}>
    ${glowAnim}
  </circle>

  <!-- Flying embers — drawn behind flame -->
  ${emberEls}

  <!-- Main flame — on top -->
  <g ${satF} style="transform-origin:${f(flameCX)}px ${f(flameCY)}px">
    ${mainFlicker}
    <g transform="translate(${f(TX)},${TY}) scale(${f(S)})">
      <path d="${FLAME}" fill="url(#fg_${uid})" opacity="${f(op)}"/>
    </g>
    <g transform="translate(${f(TX + 12*S*0.13)},${f(TY + 12*S*0.09)}) scale(${f(S * 0.73)})">
      <path d="${FLAME}" fill="url(#ig_${uid})" opacity="${f(op * 0.70)}"/>
    </g>
  </g>
</svg>`;
}
